import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { TenantGuard } from 'app/core/auth/guards/tenant.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { SuperAdminGuard } from './core/auth/guards/superadmin.guard';

// prettier-ignore
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'home'},

    // Redirect signed-in user

    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'sites'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'verify', loadChildren: () => import('app/modules/auth/verify-email/verify-email.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
            {path: 'privacy-policy', loadChildren: () => import('app/modules/landing/privacy-policy/privacy-policy.routes')},
            {path: 'terms-of-service', loadChildren: () => import('app/modules/landing/terms-of-service/terms-of-service.routes')},
            {path: 'invite/:inviteUuid', loadChildren: () => import('app/modules/auth/accept-invite/accept-invite.routes')},
            {path: 'checkout/:tenant_key', loadChildren: () => import('app/modules/landing/checkout/checkout.routes')},
            {path: 'checkout', loadChildren: () => import('app/modules/errs/error404/error-404.module').then(m => m.Error404Module)},
            {path: '**', redirectTo: 'sign-in'},
        ]
    },

    // Admin-tenant routes
    {
        path: '',
        canActivate: [TenantGuard, AuthGuard],
        canActivateChild: [TenantGuard, AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'homedash', loadChildren: () => import('app/modules/admin-tenant/homepage/homepage.module').then(m => m.HomePageModule)},
            {path: 'pedidos', loadChildren: () => import('app/modules/admin-tenant/pedidos/pedidos.module').then(m => m.PedidosModule)},
            {path: 'fidelidade', loadChildren: () => import('app/modules/admin-tenant/loyalty/loyalty.module').then(m => m.LoyaltyModule)},
            {path: 'produtos', loadChildren: () => import('app/modules/admin-tenant/products/products.module').then(m => m.ProductssModule)},
            {path: 'categorias', loadChildren: () => import('app/modules/admin-tenant/categorias/categorias.module').then(m => m.CategoriasModule)},
            {path: 'marcas', loadChildren: () => import('app/modules/admin-tenant/brands/brands.module').then(m => m.BrandsModule)},
            {path: 'grupos', loadChildren: () => import('app/modules/admin-tenant/groups/groups.module').then(m => m.GroupsModule), canActivate: [SuperAdminGuard]},
            {path: 'coupons', loadChildren: () => import('app/modules/admin-tenant/coupons/coupons.module').then(m => m.CouponsModule)},
            {path: 'faq', loadChildren: () => import('app/modules/admin-tenant/faq/faq.module').then(m => m.FaqModule)},
            {path: 'banners', loadChildren: () => import('app/modules/admin-tenant/banners/banners.module').then(m => m.BannersModule)},
            {path: 'clientes', loadChildren: () => import('app/modules/admin-tenant/clientes/clientes.module').then(m => m.ClientesModule)},
            {path: 'uploads', loadChildren: () => import('app/modules/admin-tenant/uploads/uploads.module').then(m => m.UploadsModule)},
            {path: 'medias', loadChildren: () => import('app/modules/admin-tenant/medias/medias.routes')},

            {path: 'fb-pages', loadChildren: () => import('app/modules/admin-tenant/fbconnect-pages/fb-pages.routes')},

            {path: 'settings/store', loadChildren: () => import('app/modules/admin-tenant/configs/configs.module').then(m => m.ConfigsModule)},
            {path: 'settings/users', loadChildren: () => import('app/modules/admin-tenant/users/users.module').then(m => m.UsersModule)},
            {path: 'settings/auditlogs', loadChildren: () => import('app/modules/admin-tenant/audits/auditlogs.routes')},

            {path: 'error', loadChildren: () => import('app/modules/errs/error500/error-500.module').then(m => m.Error500Module)},
            {path: 'not-found', loadChildren: () => import('app/modules/errs/error404/error-404.module').then(m => m.Error404Module)},

            // {path: 'pesquisar', loadChildren: () => import('app/modules/admin-tenant/pesquisar/pesquisar.module').then(m => m.PesquisarModule)},
            {path: 'tempcart', loadChildren: () => import('app/modules/admin-tenant/temp-cart/temp-cart.module').then(m => m.TempCartModule)},
            {path: 'dashboard', loadChildren: () => import('app/modules/admin-tenant/dashboard/dashboard.module').then(m => m.DashboardModule), canActivate: [SuperAdminGuard]},
            {path: 'configuresualoja', loadChildren: () => import('app/modules/admin/general-shop-configs/general-shop-configs.module').then(m => m.GeneralShopConfigModule), canActivate: [SuperAdminGuard]},
            {path: 'reports', loadChildren: () => import('app/modules/reports/reports.module').then(m => m.ReportsModule)},
            {path: 'drmed', loadChildren: () => import('app/modules/drmed/drmed.routes')},
            {path: 'tenant/paywall', loadChildren: () => import('app/modules/admin/paywall/paywall-tenant.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'sites', loadChildren: () => import('app/modules/admin/sites/sites.module').then(m => m.SitesModule)},
            {path: 'fbintegration', loadChildren: () => import('app/modules/admin/fbconnect/fbconnect.routes')},
            {path: 'instagram', loadChildren: () => import('app/modules/admin/instagram/instagram.module').then(m => m.InstagramModule)},
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'minhaconta', loadChildren: () => import('app/modules/admin/user-settings/settings.routes')},
            {path: 'page-builder', loadChildren: () => import('app/modules/admin/page-builder/page-builder.module').then(m => m.PageBuilderModule)},
            {path: 'dex-users', loadChildren: () => import('app/modules/admin-super/users/users.routes'), canActivate: [SuperAdminGuard]},
            {path: 'dex-subscriptions', loadChildren: () => import('app/modules/admin-super/subscriptions/subscriptions.routes'), canActivate: [SuperAdminGuard]},
            {path: 'ai-media-gallery', loadChildren: () => import('app/modules/admin/ai-media-gallery/ai-media-gallery.routes')},
            {path: 'media-editor', loadChildren: () => import('app/modules/admin/media-editor/media-editor.routes')},
            {path: 'paywall', loadChildren: () => import('app/modules/admin/paywall/paywall.routes')},
            {path: '**', redirectTo: 'sites'},
        ]
    }
];
